import { useCallback, useEffect, useState } from "react";
import {
    Button,
    Form,
    Input,
    Modal,
    Popconfirm,
    Tooltip,
    Upload,
    message,
} from "antd";
import {
    DeleteOutlined,
    EyeOutlined,
    InfoCircleOutlined,
    PlusOutlined,
    SearchOutlined,
    UploadOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "utils/axios";
import configFile from "config.json";

const API_BASE = `${configFile.PROD_SERVER_URL}/menu2`;

const mainColors = {
    bg: "#f5f7fb",
    surface: "#ffffff",
    surfaceAlt: "#f0f4ff",
    textPrimary: "#0f172a",
    textSecondary: "#64748b",
    border: "rgba(15, 23, 42, 0.08)",
    accent: "#2563eb",
    accentSoft: "#e0e7ff",
};

const toBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
    });

const Images = () => {
    const [images, setImages] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [form] = Form.useForm();

    const fetchImages = useCallback(async () => {
        setLoading(true);
        try {
            const { data: result } = await axios.post(`${API_BASE}/image/list`);
            setImages(result?.data || []);
        } catch (err) {
            message.error("Зураг татахад алдаа гарлаа");
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchImages();
    }, [fetchImages]);

    const handleUpload = useCallback(async (values) => {
        const file = fileList[0]?.originFileObj;
        if (!file) {
            message.error("Зураг сонгоно уу");
            return;
        }
        setUploading(true);
        try {
            const imgData = await toBase64(file);
            await axios.post(`${API_BASE}/image/upload`, {
                name: values.name,
                imgData,
            });
            message.success("Зураг амжилттай хуулагдлаа");
            setUploadOpen(false);
            form.resetFields();
            setFileList([]);
            fetchImages();
        } catch (err) {
            message.error("Зураг хуулахад алдаа гарлаа");
            console.log(err);
        } finally {
            setUploading(false);
        }
    }, [fetchImages, fileList, form]);

    const handleDelete = useCallback(async (img) => {
        try {
            await axios.post(`${API_BASE}/image/delete`, {
                id: img.id,
            });
            message.success("Зураг устгалаа");
            fetchImages();
        } catch (err) {
            message.error("Зураг устгахад алдаа гарлаа");
            console.log(err);
        }
    }, [fetchImages]);

    const filteredImages = images.filter((img) =>
        (img.name || "").toLowerCase().includes(searchValue.trim().toLowerCase())
    );

    return (
        <PageWrap>
            <HeaderRow>
                <TitleBlock>
                    <h1>Зургийн сан</h1>
                    <p>Бүтээгдэхүүнд ашиглах зургуудыг энд удирдана уу.</p>
                </TitleBlock>
                <HeaderMeta>
                    <div className="pill secondary">{images.length} зураг</div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setUploadOpen(true)}
                    >
                        Зураг хуулах
                    </Button>
                </HeaderMeta>
            </HeaderRow>

            <Toolbar>
                <Input
                    placeholder="Нэрээр хайх"
                    prefix={<SearchOutlined />}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    style={{ maxWidth: 280 }}
                    allowClear
                />
            </Toolbar>

            {!loading && filteredImages.length === 0 ? (
                <EmptyState>
                    <div className="title">Зураг олдсонгүй</div>
                    <p>Шинэ зураг хуулж эхлээрэй.</p>
                </EmptyState>
            ) : (
                <ImageGrid>
                    {filteredImages.map((img) => (
                        <ImageCard key={img.id ?? img.url}>
                            <Thumbnail
                                src={img.url}
                                alt={img.name}
                                onClick={() => setPreviewImage(img)}
                            />
                            <CardFooter>
                                <ImageName title={img.name}>{img.name}</ImageName>
                                <CardActions>
                                    <Button
                                        size="small"
                                        icon={<EyeOutlined />}
                                        onClick={() => setPreviewImage(img)}
                                    />
                                    <Popconfirm
                                        title="Зургийг устгах уу?"
                                        okText="Тийм"
                                        cancelText="Үгүй"
                                        onConfirm={() => handleDelete(img)}
                                    >
                                        <Button
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                        />
                                    </Popconfirm>
                                </CardActions>
                            </CardFooter>
                        </ImageCard>
                    ))}
                </ImageGrid>
            )}

            {/* Upload Modal */}
            <Modal
                open={uploadOpen}
                title="Зураг хуулах"
                onCancel={() => {
                    setUploadOpen(false);
                    form.resetFields();
                    setFileList([]);
                }}
                footer={null}
                destroyOnHidden
            >
                <Form form={form} layout="vertical" onFinish={handleUpload}>
                    <Form.Item
                        name="name"
                        label="Нэр"
                        rules={[{ required: true, message: "Нэр оруулна уу" }]}
                    >
                        <Input placeholder="Зургийн нэр" />
                    </Form.Item>
                    <Form.Item
                        label={(
                            <span>
                                Зураг{" "}
                                <Tooltip title="3:2 харьцаатай зураг сонгоно уу Жишээлвэл (300px:200px)">
                                    <InfoCircleOutlined style={{ color: "#64748b" }} />
                                </Tooltip>
                            </span>
                        )}
                        required
                    >
                        <Upload
                            listType="picture"
                            maxCount={1}
                            beforeUpload={() => false}
                            fileList={fileList}
                            onChange={({ fileList: next }) => setFileList(next)}
                            accept="image/png,image/jpeg"
                        >
                            <Button icon={<UploadOutlined />}>Файл сонгох</Button>
                        </Upload>
                    </Form.Item>
                    <ModalFooter>
                        <Button
                            onClick={() => {
                                setUploadOpen(false);
                                form.resetFields();
                                setFileList([]);
                            }}
                        >
                            Буцах
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={uploading}
                            disabled={fileList.length === 0}
                        >
                            Хуулах
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Preview Modal */}
            <Modal
                open={previewImage !== null}
                title={previewImage?.name || "Preview"}
                footer={null}
                onCancel={() => setPreviewImage(null)}
                centered
            >
                {previewImage && (
                    <img
                        src={previewImage.url}
                        alt={previewImage.name}
                        style={{ width: "100%" }}
                    />
                )}
            </Modal>
        </PageWrap>
    );
};

export default Images;

const PageWrap = styled.div`
  padding: 28px;
  min-height: 100vh;
  background: radial-gradient(circle at top, #fdf2f8 0%, #f5f7fb 38%, #eef2ff 100%);
  color: ${mainColors.textPrimary};
  font-family: "Sora", "SF Pro Text", "Segoe UI", sans-serif;
  --radius-lg: 8px;
  --radius-md: 6px;
  --radius-sm: 5px;

  .ant-btn,
  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector {
    border-radius: var(--radius-sm) !important;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 16px;
  padding: 18px 22px;
  border-radius: 16px;
  border: 1px solid rgba(22, 119, 255, 0.18);
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 18px 50px rgba(33, 49, 63, 0.1);
  backdrop-filter: blur(8px);

  h1 {
    margin: 0 0 6px 0;
    font-size: 22px;
    letter-spacing: -0.02em;
  }

  p {
    margin: 0;
    color: #6b7a88;
  }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderMeta = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;

  .pill {
    background: ${mainColors.surface};
    border: 1px solid ${mainColors.border};
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 12px;
    color: ${mainColors.textPrimary};
    box-shadow: 0 8px 16px rgba(15, 23, 42, 0.06);
  }

  .pill.secondary {
    background: ${mainColors.accentSoft};
    color: ${mainColors.accent};
  }
`;

const Toolbar = styled.div`
  margin-bottom: 20px;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const ImageCard = styled.div`
  background: ${mainColors.surface};
  border: 1px solid ${mainColors.border};
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
  transition: box-shadow 0.2s, transform 0.2s;

  &:hover {
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
    transform: translateY(-2px);
  }
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
  display: block;
  cursor: pointer;
  background: ${mainColors.surfaceAlt};
`;

const CardFooter = styled.div`
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ImageName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${mainColors.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const CardActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  border: 1px dashed ${mainColors.border};
  border-radius: var(--radius-lg);
  padding: 60px 36px;
  text-align: center;
  background: ${mainColors.surfaceAlt};
  color: ${mainColors.textSecondary};

  .title {
    font-size: 18px;
    font-weight: 600;
    color: ${mainColors.textPrimary};
    margin-bottom: 8px;
  }

  p {
    margin: 0;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;
